import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./PixelsPage.css";
import { useLanguage } from "../contexts/LanguageContext";
import { useTheme } from "../contexts/ThemeContext";
import { useFirebase } from "../contexts/FirebaseContext";
import {
  DEFAULT_GRID,
  MAX_GRID,
  MIN_GRID,
  PIXEL_AVATAR_STORAGE_KEY,
  clampGridSize,
  deserializePixelAvatar,
  emptyGrid,
  normalizeHex,
  readLocalPixelAvatar,
  serializePixelAvatar,
} from "../utils/pixelAvatar";
import {
  applyLocalHearts,
  deletePixelPost,
  postsForTab,
  publishPixelPost,
  setLocalHeart,
  subscribeMyPixelPosts,
  subscribePixelPosts,
  togglePixelPostHeart,
} from "../services/pixelPosts";

const SQUARE_SIZE = 360;
const SAVE_DELAY_MS = 400;

const THEME_PAINT = {
  red: "#FF0000",
  purple: "#F4D35E",
  pink: "#A78BFA",
  yellow: "#EC4899",
  blue: "#3B82F6",
  green: "#22C55E",
};

const THEME_DEFAULTS = new Set(
  Object.values(THEME_PAINT).map((value) => value.toUpperCase())
);
THEME_DEFAULTS.add("#7C3AED");

const createDefaultPixels = (paint) => {
  const next = emptyGrid(DEFAULT_GRID);
  next[0] = paint;
  return next;
};

const PRESET_COLORS = [
  "#FFFFFF",
  "#9CA3AF",
  "#111827",
  "#EF4444",
  "#F97316",
  "#F59E0B",
  "#F4D35E",
  "#22C55E",
  "#06B6D4",
  "#3B82F6",
  "#A78BFA",
  "#EC4899",
];

const hexToHsv = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  let h = 0;
  if (delta !== 0) {
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : delta / max, v: max };
};

const hsvToHex = (h, s, v) => {
  const chroma = v * s;
  const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - chroma;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [chroma, x, 0];
  else if (h < 120) [r, g, b] = [x, chroma, 0];
  else if (h < 180) [r, g, b] = [0, chroma, x];
  else if (h < 240) [r, g, b] = [0, x, chroma];
  else if (h < 300) [r, g, b] = [x, 0, chroma];
  else [r, g, b] = [chroma, 0, x];
  const toHex = (channel) =>
    Math.round((channel + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
};

const isLightHex = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 180;
};

const hueColor = (h) => hsvToHex(h, 1, 1);

const ColourPicker = ({ color, disabled, label, onChange }) => {
  const wrapRef = useRef(null);
  const pickerRef = useRef(null);
  const hsvRef = useRef(hexToHsv(color));
  const [open, setOpen] = useState(false);
  const [hsv, setHsv] = useState(() => hexToHsv(color));
  const [pos, setPos] = useState({ top: 0, left: 0 });

  hsvRef.current = hsv;

  const commitHsv = useCallback(
    (next) => {
      hsvRef.current = next;
      setHsv(next);
      onChange(hsvToHex(next.h, next.s, next.v));
    },
    [onChange]
  );

  const placePicker = useCallback(() => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 228;
    const left = Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12));
    const estimatedHeight = 260;
    const below = rect.bottom + 8;
    const top =
      below + estimatedHeight > window.innerHeight - 12
        ? Math.max(12, rect.top - estimatedHeight - 8)
        : below;
    setPos({ top, left });
  }, []);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useLayoutEffect(() => {
    if (!open) return;
    placePicker();
  }, [open, placePicker]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event) => {
      if (wrapRef.current?.contains(event.target)) return;
      if (pickerRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", placePicker);
    window.addEventListener("scroll", placePicker, true);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", placePicker);
      window.removeEventListener("scroll", placePicker, true);
    };
  }, [open, placePicker]);

  const startDrag = (event, readValue) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget;
    const move = (nextEvent) => {
      readValue(nextEvent, target);
    };
    move(event);
    target.setPointerCapture(event.pointerId);
    const stop = () => {
      target.removeEventListener("pointermove", move);
      target.removeEventListener("pointerup", stop);
      target.removeEventListener("pointercancel", stop);
    };
    target.addEventListener("pointermove", move);
    target.addEventListener("pointerup", stop);
    target.addEventListener("pointercancel", stop);
  };

  const readSv = (event, target) => {
    const rect = target.getBoundingClientRect();
    const s = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const v = Math.min(1, Math.max(0, 1 - (event.clientY - rect.top) / rect.height));
    commitHsv({ ...hsvRef.current, s, v });
  };

  const readHue = (event, target) => {
    const rect = target.getBoundingClientRect();
    const h = Math.min(359.99, Math.max(0, ((event.clientX - rect.left) / rect.width) * 360));
    commitHsv({ ...hsvRef.current, h });
  };

  return (
    <div className="px-colour-picker" ref={wrapRef}>
      <button
        type="button"
        className={`px-colour-swatch-wrap ${disabled ? "is-preview" : ""} ${
          open ? "is-open" : ""
        }`}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          if (disabled) return;
          if (open) {
            setOpen(false);
            return;
          }
          setHsv(hexToHsv(color));
          setOpen(true);
        }}
      >
        <span
          className="px-colour-swatch"
          style={{ backgroundColor: color }}
          aria-hidden="true"
        />
      </button>
      {open && !disabled && (
        <div
          className="px-picker"
          ref={pickerRef}
          role="dialog"
          aria-label={label}
          style={{ top: pos.top, left: pos.left }}
        >
          <div
            className="px-picker-sv"
            style={{
              background: `linear-gradient(to bottom, transparent, #000), linear-gradient(to right, #fff, ${hueColor(
                hsv.h
              )})`,
            }}
            onPointerDown={(event) => startDrag(event, readSv)}
          >
            <span
              className="px-picker-thumb px-picker-sv-thumb"
              style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }}
            />
          </div>
          <div
            className="px-picker-hue"
            onPointerDown={(event) => startDrag(event, readHue)}
          >
            <span
              className="px-picker-thumb px-picker-hue-thumb"
              style={{ left: `${(hsv.h / 360) * 100}%` }}
            />
          </div>
          <div className="px-picker-chips">
            {PRESET_COLORS.map((preset) => (
              <button
                key={preset}
                type="button"
                className={`px-picker-chip ${
                  color.toUpperCase() === preset ? "is-active" : ""
                } ${isLightHex(preset) ? "is-light" : ""}`}
                style={{ backgroundColor: preset }}
                aria-label={preset}
                onClick={() => {
                  onChange(preset);
                  setHsv(hexToHsv(preset));
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MyAvatarMark = () => (
  <svg
    className="px-mark"
    width="36"
    height="36"
    viewBox="0 0 36 36"
    aria-hidden="true"
  >
    <rect
      className="px-mark-bg"
      x="0.75"
      y="0.75"
      width="34.5"
      height="34.5"
      rx="9"
    />
    <text
      className="px-mark-text"
      x="18"
      y="23.5"
      textAnchor="middle"
      fontSize="13"
      fontWeight="800"
      fontFamily="DM Sans, Inter, sans-serif"
      letterSpacing="0.4"
    >
      MY
    </text>
  </svg>
);

const GridSizeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <rect x="1" y="1" width="4" height="4" rx="0.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <rect x="6" y="1" width="4" height="4" rx="0.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <rect x="11" y="1" width="4" height="4" rx="0.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <rect x="1" y="6" width="4" height="4" rx="0.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <rect x="6" y="6" width="4" height="4" rx="0.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <rect x="11" y="6" width="4" height="4" rx="0.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <rect x="1" y="11" width="4" height="4" rx="0.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <rect x="6" y="11" width="4" height="4" rx="0.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <rect x="11" y="11" width="4" height="4" rx="0.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
  </svg>
);

const CopyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <rect
      x="8"
      y="8"
      width="11"
      height="11"
      rx="2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M6 16 H5 a2 2 0 0 1 -2 -2 V5 a2 2 0 0 1 2 -2 h9 a2 2 0 0 1 2 2 v1"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const ChevronIcon = ({ direction }) => {
  const paths = {
    up: "M6 14 L12 8 L18 14",
    down: "M6 10 L12 16 L18 10",
    left: "M14 6 L8 12 L14 18",
    right: "M10 6 L16 12 L10 18",
  };
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d={paths[direction]}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const PencilIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M4 20 L4 16 L16 4 L20 8 L8 20 Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M13.5 6.5 L17.5 10.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    />
  </svg>
);

const SparklesIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 3 L13.4 8.2 L18.5 9.5 L13.4 10.8 L12 16 L10.6 10.8 L5.5 9.5 L10.6 8.2 Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path
      d="M18.5 14 L19.2 16.4 L21.5 17 L19.2 17.6 L18.5 20 L17.8 17.6 L15.5 17 L17.8 16.4 Z"
      fill="currentColor"
    />
  </svg>
);

const UndoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M8 7 H6 a1 1 0 0 0 -1 1 v2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M7 8 a7 7 0 1 1 -0.2 8"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M8 7 L5 5 M8 7 L5 10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RedoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M16 7 H18 a1 1 0 0 1 1 1 v2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M17 8 a7 7 0 1 0 0.2 8"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M16 7 L19 5 M16 7 L19 10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DeleteIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M7 7 L17 17 M17 7 L7 17"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const DisplayIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <rect
      x="1.5"
      y="2.5"
      width="13"
      height="9"
      rx="1.4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
    />
    <path
      d="M5 13.5 H11"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
    <path
      d="M8 11.5 V13.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
  </svg>
);

const AlbumIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <rect
      x="1.5"
      y="1.5"
      width="6"
      height="6"
      rx="1.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
    />
    <rect
      x="8.5"
      y="1.5"
      width="6"
      height="6"
      rx="1.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
    />
    <rect
      x="1.5"
      y="8.5"
      width="6"
      height="6"
      rx="1.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
    />
    <rect
      x="8.5"
      y="8.5"
      width="6"
      height="6"
      rx="1.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
    />
  </svg>
);

const NewIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M8 3 V13 M3 8 H13"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

const AllIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <rect x="1.5" y="1.5" width="5" height="5" rx="1" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <rect x="9.5" y="1.5" width="5" height="5" rx="1" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <rect x="1.5" y="9.5" width="5" height="5" rx="1" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <rect x="9.5" y="9.5" width="5" height="5" rx="1" fill="none" stroke="currentColor" strokeWidth="1.3" />
  </svg>
);

const PopularIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M8 2.2 L9.7 6 H14 L10.8 8.7 L12.2 13 L8 10.6 L3.8 13 L5.2 8.7 L2 6 H6.3 Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

const HideGridIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <rect x="1" y="1" width="4" height="4" rx="0.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <rect x="6" y="1" width="4" height="4" rx="0.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <rect x="11" y="1" width="4" height="4" rx="0.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <rect x="1" y="6" width="4" height="4" rx="0.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <rect x="6" y="6" width="4" height="4" rx="0.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <rect x="11" y="6" width="4" height="4" rx="0.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <rect x="1" y="11" width="4" height="4" rx="0.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <rect x="6" y="11" width="4" height="4" rx="0.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <rect x="11" y="11" width="4" height="4" rx="0.6" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <path
      d="M2.5 13.5 L13.5 2.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const PublishIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M8 11.5 V3.5 M8 3.5 L5 6.5 M8 3.5 L11 6.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 12.5 V13.2 a0.8 0.8 0 0 0 0.8 0.8 h8.4 a0.8 0.8 0 0 0 0.8 -0.8 V12.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

const HeartIcon = ({ filled }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 20.4 S4.8 15.4 2.7 11.2 C0.6 7.2 2.2 3.6 5.6 2.8 C7.8 2.3 10 3.1 12 5.6 C14 3.1 16.2 2.3 18.4 2.8 C21.8 3.6 23.4 7.2 21.3 11.2 C19.2 15.4 12 20.4 12 20.4 Z"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const PixelArtGrid = ({ gridSize, pixels, size }) => {
  // Hidden-grid preview for published pictures.
  const cells = Array.from({ length: gridSize * gridSize }, (_, index) => index);
  return (
    <div
      className="px-grid is-hidden-grid is-dense"
      style={{
        width: size,
        height: size,
        gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
        gridTemplateRows: `repeat(${gridSize}, 1fr)`,
      }}
    >
      {cells.map((index) => {
        const fill = pixels[index];
        return (
          <div
            key={index}
            className={`px-cell ${fill ? "is-filled" : ""}`}
            style={fill ? { backgroundColor: fill } : undefined}
          />
        );
      })}
    </div>
  );
};

const PixelPostCard = ({
  post,
  userId,
  heartInteractive,
  busy,
  onHeart,
  heartLabel,
  unheartLabel,
  rank = null,
  canDelete = false,
  onDelete,
  deleteLabel,
}) => {
  const art = deserializePixelAvatar({
    gridSize: post.gridSize,
    cells: post.cells,
  });
  const prevCountRef = useRef(post.heartCount);
  const [pop, setPop] = useState(null);

  useEffect(() => {
    const prev = prevCountRef.current;
    prevCountRef.current = post.heartCount;
    if (post.heartCount <= prev) return undefined;
    setPop(`+${post.heartCount - prev}`);
    const timeoutId = window.setTimeout(() => setPop(null), 700);
    return () => window.clearTimeout(timeoutId);
  }, [post.heartCount]);

  if (!art) return null;
  const liked = Boolean(userId && post.likedBy.includes(userId));
  const showFilled = heartInteractive ? liked : post.heartCount > 0;
  return (
    <article className="px-post">
      <div className="px-post-frame">
        {rank != null && (
          <span className="px-post-rank" aria-hidden="true">
            {rank}
          </span>
        )}
        <PixelArtGrid
          gridSize={art.gridSize}
          pixels={art.pixels}
          size="100%"
        />
      </div>
      <div className="px-post-hearts">
        {heartInteractive ? (
          <button
            type="button"
            className={`px-heart-btn ${liked ? "is-active" : ""}`}
            aria-pressed={liked}
            aria-label={liked ? unheartLabel : heartLabel}
            disabled={!userId || busy}
            onClick={() => onHeart(post)}
          >
            <HeartIcon filled={liked} />
          </button>
        ) : (
          <span
            className={`px-heart-mark ${showFilled ? "is-active" : ""}`}
            aria-hidden="true"
          >
            <HeartIcon filled={showFilled} />
          </span>
        )}
        <span className="px-heart-count">{post.heartCount}</span>
        {pop && (
          <span className="px-heart-pop" aria-hidden="true">
            {pop}
          </span>
        )}
        {canDelete ? (
          <button
            type="button"
            className="px-post-delete"
            aria-label={deleteLabel}
            disabled={busy || !userId}
            onClick={() => onDelete(post)}
          >
            <DeleteIcon />
          </button>
        ) : null}
      </div>
    </article>
  );
};

const ClearIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M5 7 H19"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M9 7 V5.5 a1.5 1.5 0 0 1 1.5 -1.5 h3 a1.5 1.5 0 0 1 1.5 1.5 V7"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M8 7 L8.6 19 a1.5 1.5 0 0 0 1.5 1.4 h4 a1.5 1.5 0 0 0 1.5 -1.4 L16 7"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const PixelsPage = () => {
  const { t } = useLanguage();
  const { mode: themeMode } = useTheme();
  const { loading, pixelAvatar, savePixelAvatar, userId, displayName } =
    useFirebase();
  const themePaint = THEME_PAINT[themeMode] || THEME_PAINT.purple;
  const [initialAvatar] = useState(
    () => deserializePixelAvatar(pixelAvatar) || readLocalPixelAvatar()
  );
  const [mode, setMode] = useState(initialAvatar?.mode || "simple");
  const [color, setColor] = useState(initialAvatar?.color || themePaint);
  const [hexDraft, setHexDraft] = useState(initialAvatar?.color || themePaint);
  const [gridSize, setGridSize] = useState(
    initialAvatar?.gridSize || DEFAULT_GRID
  );
  const [gridDraft, setGridDraft] = useState(
    String(initialAvatar?.gridSize || DEFAULT_GRID)
  );
  const [gridHidden, setGridHidden] = useState(
    Boolean(initialAvatar?.gridHidden)
  );
  const [view, setView] = useState("avatar");
  const [displayTab, setDisplayTab] = useState("new");
  const [published, setPublished] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [posts, setPosts] = useState([]);
  const [busyHeartId, setBusyHeartId] = useState(null);
  const [busyDeleteId, setBusyDeleteId] = useState(null);
  const [cursor, setCursor] = useState({ x: 0, y: 0 });
  const [pixels, setPixels] = useState(
    () => initialAvatar?.pixels || createDefaultPixels(themePaint)
  );
  const hydratedRef = useRef(false);
  const skipSaveRef = useRef(false);
  const userEditedRef = useRef(false);
  const dirtyRef = useRef(false);
  const publishedTimerRef = useRef(null);
  const saveFnRef = useRef(savePixelAvatar);
  const stateRef = useRef({ gridSize, mode, color, pixels, gridHidden });
  saveFnRef.current = savePixelAvatar;
  stateRef.current = { gridSize, mode, color, pixels, gridHidden };

  const cells = Array.from({ length: gridSize * gridSize }, (_, index) => index);
  const isAdvanced = mode === "advanced";
  const isAvatar = view === "avatar";
  const isDisplay = view === "display";
  const isAlbum = view === "album";
  const selectedIndex = cursor.y * gridSize + cursor.x;
  const showCellDots = gridSize < 24;

  const persistNow = useCallback((nextState = stateRef.current) => {
    const payload = serializePixelAvatar(nextState);
    if (!payload) return;
    try {
      window.localStorage.setItem(
        PIXEL_AVATAR_STORAGE_KEY,
        JSON.stringify(payload)
      );
    } catch {
      // ignore quota / private mode
    }
    dirtyRef.current = false;
    if (typeof saveFnRef.current === "function") {
      saveFnRef.current(payload);
    }
  }, []);

  const markEdited = useCallback(() => {
    userEditedRef.current = true;
    setPublished(false);
  }, []);

  const publishAvatar = useCallback(async () => {
    if (publishing) return;
    persistNow();
    const payload = serializePixelAvatar(stateRef.current);
    if (!payload || !userId) return;
    setPublishing(true);
    try {
      await publishPixelPost({
        userId,
        displayName,
        gridSize: payload.gridSize,
        cells: payload.cells,
      });
      setPublished(true);
      setView("display");
      setDisplayTab("new");
      if (publishedTimerRef.current) {
        window.clearTimeout(publishedTimerRef.current);
      }
      publishedTimerRef.current = window.setTimeout(() => {
        setPublished(false);
        publishedTimerRef.current = null;
      }, 1600);
    } catch (error) {
      console.error("Error publishing pixel picture:", error);
    } finally {
      setPublishing(false);
    }
  }, [displayName, persistNow, publishing, userId]);

  const giveHeart = useCallback(
    async (post) => {
      if (!userId || !post?.id || busyHeartId) return;
      const liked = post.likedBy.includes(userId);
      if (liked) {
        setLocalHeart(userId, post.id, false);
      } else {
        setLocalHeart(userId, post.id, true);
      }
      setBusyHeartId(post.id);
      setPosts((current) => {
        const next = current.map((item) => {
          if (item.id !== post.id) return item;
          const already = item.likedBy.includes(userId);
          if (already === !liked) return item;
          const likedBy = liked
            ? item.likedBy.filter((id) => id !== userId)
            : [...item.likedBy, userId];
          return {
            ...item,
            likedBy,
            heartCount: Math.max(0, item.heartCount + (liked ? -1 : 1)),
          };
        });
        if (displayTab === "popular") {
          return postsForTab(next, "popular");
        }
        return next;
      });
      try {
        await togglePixelPostHeart({
          postId: post.id,
          userId,
          liked,
        });
      } catch (error) {
        console.error("Error updating heart:", error);
        setLocalHeart(userId, post.id, liked);
        setPosts((current) =>
          current.map((item) => (item.id === post.id ? post : item))
        );
      } finally {
        setBusyHeartId(null);
      }
    },
    [busyHeartId, displayTab, userId]
  );

  const removePost = useCallback(
    async (post) => {
      if (!userId || !post?.id || post.userId !== userId || busyDeleteId) return;
      setBusyDeleteId(post.id);
      setPosts((current) => current.filter((item) => item.id !== post.id));
      try {
        await deletePixelPost({ postId: post.id, userId });
      } catch (error) {
        console.error("Error deleting pixel picture:", error);
        setPosts((current) =>
          [...current, post].sort((a, b) => b.createdAtMs - a.createdAtMs)
        );
      } finally {
        setBusyDeleteId(null);
      }
    },
    [busyDeleteId, userId]
  );

  useEffect(() => {
    const apply = (next) => setPosts(applyLocalHearts(next, userId));
    if (isDisplay) return subscribePixelPosts(displayTab, apply);
    if (isAlbum) return subscribeMyPixelPosts(userId, apply);
    return undefined;
  }, [displayTab, isAlbum, isDisplay, userId]);

  useEffect(
    () => () => {
      if (publishedTimerRef.current) {
        window.clearTimeout(publishedTimerRef.current);
      }
    },
    []
  );

  const applyAvatar = useCallback((stored) => {
    const decoded = deserializePixelAvatar(stored);
    if (!decoded) return false;
    setGridSize(decoded.gridSize);
    setGridDraft(String(decoded.gridSize));
    setMode(decoded.mode);
    setColor(decoded.color);
    setHexDraft(decoded.color);
    setGridHidden(Boolean(decoded.gridHidden));
    setPixels(decoded.pixels);
    setCursor((current) => ({
      x: Math.min(decoded.gridSize - 1, current.x),
      y: Math.min(decoded.gridSize - 1, current.y),
    }));
    return true;
  }, []);

  useEffect(() => {
    if (loading || hydratedRef.current) return;
    hydratedRef.current = true;
    if (userEditedRef.current) {
      persistNow();
    } else if (pixelAvatar) {
      skipSaveRef.current = true;
      applyAvatar(pixelAvatar);
    } else if (initialAvatar) {
      persistNow(initialAvatar);
    }
  }, [applyAvatar, initialAvatar, loading, persistNow, pixelAvatar]);

  useEffect(() => {
    if (!hydratedRef.current) return undefined;
    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      return undefined;
    }
    dirtyRef.current = true;
    const timer = window.setTimeout(() => {
      persistNow();
    }, SAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [color, gridHidden, gridSize, mode, persistNow, pixels]);

  useEffect(() => {
    const flush = () => {
      if (!dirtyRef.current) return;
      persistNow();
    };
    window.addEventListener("pagehide", flush);
    return () => {
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, [persistNow]);

  useEffect(() => {
    setColor((current) => {
      if (!THEME_DEFAULTS.has(current.toUpperCase())) return current;
      return themePaint;
    });
    setHexDraft((current) => {
      if (!THEME_DEFAULTS.has(current.toUpperCase())) return current;
      return themePaint;
    });
  }, [themePaint]);

  const applyColor = (next) => {
    const normalized = normalizeHex(next);
    if (!normalized) return;
    markEdited();
    setColor(normalized);
    setHexDraft(normalized);
  };

  const applyGridSize = (raw) => {
    const next = clampGridSize(raw);
    if (!next) {
      setGridDraft(String(gridSize));
      return;
    }
    setGridDraft(String(next));
    if (next === gridSize) return;
    markEdited();
    setGridSize(next);
    setPixels(emptyGrid(next));
    setCursor({ x: 0, y: 0 });
  };

  const moveCursor = useCallback((dx, dy) => {
    setCursor((current) => ({
      x: Math.min(gridSize - 1, Math.max(0, current.x + dx)),
      y: Math.min(gridSize - 1, Math.max(0, current.y + dy)),
    }));
  }, [gridSize]);

  const fillAt = useCallback(
    (x, y) => {
      const index = y * gridSize + x;
      setPixels((current) => {
        if (current[index] === color) return current;
        markEdited();
        const next = [...current];
        next[index] = color;
        return next;
      });
    },
    [color, gridSize, markEdited]
  );

  const eraseAt = useCallback(
    (x, y) => {
      const index = y * gridSize + x;
      setPixels((current) => {
        if (current[index] == null) return current;
        markEdited();
        const next = [...current];
        next[index] = null;
        return next;
      });
    },
    [gridSize, markEdited]
  );

  const handleCellClick = (x, y) => {
    setCursor({ x, y });
    fillAt(x, y);
  };

  const clearGrid = () => {
    markEdited();
    setPixels(emptyGrid(gridSize));
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      if (view !== "avatar") return;
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;

      if (event.key === "ArrowUp") {
        event.preventDefault();
        moveCursor(0, -1);
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        moveCursor(0, 1);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        moveCursor(-1, 0);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        moveCursor(1, 0);
      } else if (event.key === " " || event.key === "Enter") {
        if (event.target.closest?.(".px-dpad-btn")) return;
        event.preventDefault();
        fillAt(cursor.x, cursor.y);
      } else if (event.key === "Delete" || event.key === "Backspace") {
        event.preventDefault();
        eraseAt(cursor.x, cursor.y);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [cursor.x, cursor.y, eraseAt, fillAt, moveCursor, view]);

  return (
    <div className={`px-page ${isAdvanced ? "is-advanced" : "is-simple"}`}>
      <div className="px-card">
        <header className="px-header">
          <div className="px-toolbar" role="toolbar" aria-label={t("navPixels")}>
            <button
              type="button"
              className={`px-grid-toggle ${gridHidden ? "is-active" : ""}`}
              aria-pressed={gridHidden}
              onClick={() => {
                markEdited();
                setGridHidden((hidden) => !hidden);
              }}
            >
              <HideGridIcon />
              <span>{gridHidden ? t("pixelsShowGrid") : t("pixelsHideGrid")}</span>
            </button>

            <button
              type="button"
              className={`px-publish ${published ? "is-active" : ""}`}
              onClick={publishAvatar}
              disabled={publishing || !userId}
            >
              <PublishIcon />
              <span>{published ? t("pixelsPublished") : t("pixelsPublish")}</span>
            </button>

            <label className="px-grid-size">
              <GridSizeIcon />
              <span>{t("pixelsGridSize")}</span>
              <input
                type="range"
                className="px-grid-size-range"
                min={MIN_GRID}
                max={MAX_GRID}
                value={gridSize}
                onChange={(event) => applyGridSize(event.target.value)}
                aria-label={t("pixelsGridSize")}
              />
              <input
                type="number"
                className="px-grid-size-input"
                min={MIN_GRID}
                max={MAX_GRID}
                value={gridDraft}
                onChange={(event) => setGridDraft(event.target.value)}
                onBlur={() => applyGridSize(gridDraft)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.currentTarget.blur();
                  }
                }}
              />
            </label>

            <button
              type="button"
              className={`px-view-btn ${view === "avatar" ? "is-active" : ""}`}
              aria-pressed={view === "avatar"}
              onClick={() => setView("avatar")}
            >
              <MyAvatarMark />
              <span>{t("pixelsTitle")}</span>
            </button>

            <button
              type="button"
              className={`px-view-btn ${view === "display" ? "is-active" : ""}`}
              aria-pressed={view === "display"}
              onClick={() => setView("display")}
            >
              <DisplayIcon />
              <span>{t("pixelsDisplay")}</span>
            </button>

            <button
              type="button"
              className={`px-view-btn ${view === "album" ? "is-active" : ""}`}
              aria-pressed={view === "album"}
              onClick={() => setView("album")}
            >
              <AlbumIcon />
              <span>{t("pixelsAlbum")}</span>
            </button>
          </div>

          {isAvatar && (
            <div className="px-colour">
              <span className="px-colour-label">{t("pixelsColourPicker")}</span>
              <ColourPicker
                color={color}
                disabled={isAdvanced}
                label={t("pixelsColourPicker")}
                onChange={applyColor}
              />
            </div>
          )}
        </header>

        {isAvatar ? (
        <>
        <div className="px-body">
          <section className="px-canvas" aria-label={`${gridSize} x ${gridSize}`}>
            <p className="px-size-label">
              {gridSize} x {gridSize}
            </p>
            <div
              className={`px-grid ${showCellDots ? "" : "is-dense"} ${
                gridHidden ? "is-hidden-grid" : ""
              }`}
              style={{
                width: SQUARE_SIZE,
                height: SQUARE_SIZE,
                gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
                gridTemplateRows: `repeat(${gridSize}, 1fr)`,
              }}
            >
              {cells.map((index) => {
                const x = index % gridSize;
                const y = Math.floor(index / gridSize);
                const selected = index === selectedIndex;
                const fill = pixels[index];
                return (
                  <button
                    key={index}
                    type="button"
                    className={`px-cell ${selected ? "is-selected" : ""} ${
                      fill ? "is-filled" : ""
                    }`}
                    style={fill ? { backgroundColor: fill } : undefined}
                    onClick={() => handleCellClick(x, y)}
                    aria-label={`Pixel ${x + 1}, ${y + 1}`}
                    aria-pressed={selected}
                  />
                );
              })}
            </div>
          </section>

          <aside className="px-tools">
            <div className="px-tools-scroll">
            <section className="px-tool px-tool--hex" aria-hidden={!isAdvanced}>
              <h2 className="px-tool-label">{t("pixelsHex")}</h2>
              <div className="px-hex">
                <input
                  className="px-hex-input"
                  value={hexDraft}
                  onChange={(event) => {
                    const next = event.target.value;
                    setHexDraft(next);
                    const normalized = normalizeHex(next);
                    if (normalized) setColor(normalized);
                  }}
                  onBlur={() => setHexDraft(color)}
                  aria-label={t("pixelsHex")}
                  spellCheck={false}
                  tabIndex={isAdvanced ? 0 : -1}
                  readOnly={!isAdvanced}
                />
                <span className="px-hex-copy" aria-hidden="true">
                  <CopyIcon />
                </span>
              </div>
            </section>

            <section className="px-tool">
              <h2 className="px-tool-label">{t("pixelsCursor")}</h2>
              <div className="px-dpad">
                <button
                  type="button"
                  className="px-dpad-btn px-dpad-up"
                  aria-label="Up"
                  onClick={() => moveCursor(0, -1)}
                >
                  <ChevronIcon direction="up" />
                </button>
                <button
                  type="button"
                  className="px-dpad-btn px-dpad-left"
                  aria-label="Left"
                  onClick={() => moveCursor(-1, 0)}
                >
                  <ChevronIcon direction="left" />
                </button>
                <button
                  type="button"
                  className="px-dpad-center"
                  aria-label="Fill pixel"
                  style={{ backgroundColor: color }}
                  onClick={() => fillAt(cursor.x, cursor.y)}
                />
                <button
                  type="button"
                  className="px-dpad-btn px-dpad-right"
                  aria-label="Right"
                  onClick={() => moveCursor(1, 0)}
                >
                  <ChevronIcon direction="right" />
                </button>
                <button
                  type="button"
                  className="px-dpad-btn px-dpad-down"
                  aria-label="Down"
                  onClick={() => moveCursor(0, 1)}
                >
                  <ChevronIcon direction="down" />
                </button>
              </div>
            </section>

            <section className="px-tool">
              <h2 className="px-tool-label">{t("pixelsMode")}</h2>
              <div className="px-modes">
                <button
                  type="button"
                  className={`px-mode ${mode === "simple" ? "is-active" : ""}`}
                  aria-pressed={mode === "simple"}
                  onClick={() => {
                    markEdited();
                    setMode("simple");
                  }}
                >
                  <PencilIcon />
                  <span>{t("pixelsSimple")}</span>
                </button>
                <button
                  type="button"
                  className={`px-mode ${mode === "advanced" ? "is-active" : ""}`}
                  aria-pressed={mode === "advanced"}
                  onClick={() => {
                    markEdited();
                    setMode("advanced");
                  }}
                >
                  <SparklesIcon />
                  <span>{t("pixelsAdvanced")}</span>
                </button>
              </div>
            </section>

            <section className="px-tool px-tool--shortcuts">
              <h2 className="px-tool-label">{t("pixelsShortcuts")}</h2>
              <ul className="px-shortcuts">
                <li>
                  <kbd>↑</kbd>
                  <span>{t("pixelsShortcutUp")}</span>
                </li>
                <li>
                  <kbd>↓</kbd>
                  <span>{t("pixelsShortcutDown")}</span>
                </li>
                <li>
                  <kbd>←</kbd>
                  <span>{t("pixelsShortcutLeft")}</span>
                </li>
                <li>
                  <kbd>→</kbd>
                  <span>{t("pixelsShortcutRight")}</span>
                </li>
                <li>
                  <kbd>Space</kbd>
                  <span>{t("pixelsShortcutPut")}</span>
                </li>
                <li>
                  <kbd>Enter</kbd>
                  <span>{t("pixelsShortcutPut")}</span>
                </li>
                <li>
                  <kbd>Del</kbd>
                  <span>{t("pixelsShortcutDelete")}</span>
                </li>
              </ul>
            </section>
            </div>

            <section className="px-tool px-tool--actions">
              <div className="px-actions">
                <button type="button" className="px-action">
                  <UndoIcon />
                  <span>{t("pixelsUndo")}</span>
                </button>
                <button type="button" className="px-action" disabled>
                  <RedoIcon />
                  <span>{t("pixelsRedo")}</span>
                </button>
                <button
                  type="button"
                  className="px-action"
                  onClick={() => eraseAt(cursor.x, cursor.y)}
                >
                  <DeleteIcon />
                  <span>{t("pixelsDelete")}</span>
                </button>
                <button
                  type="button"
                  className="px-action px-action--clear"
                  onClick={clearGrid}
                >
                  <ClearIcon />
                  <span>{t("pixelsClear")}</span>
                </button>
              </div>
            </section>
          </aside>
        </div>

        <footer className="px-footer">
          <div className="px-selected">
            <span
              className="px-selected-swatch"
              style={{
                borderColor: color,
                backgroundColor: pixels[selectedIndex] || color,
              }}
              aria-hidden="true"
            />
            <span>{t("pixelsSelected")}</span>
          </div>
          <p className="px-hint">{t("pixelsHint")}</p>
        </footer>
        </>
        ) : isDisplay ? (
          <div className="px-display">
            <div
              className="px-display-toolbar"
              role="toolbar"
              aria-label={t("pixelsDisplay")}
            >
              <button
                type="button"
                className={`px-display-tab ${
                  displayTab === "new" ? "is-active" : ""
                }`}
                aria-pressed={displayTab === "new"}
                onClick={() => setDisplayTab("new")}
              >
                <NewIcon />
                <span>{t("pixelsNew")}</span>
              </button>
              <button
                type="button"
                className={`px-display-tab ${
                  displayTab === "all" ? "is-active" : ""
                }`}
                aria-pressed={displayTab === "all"}
                onClick={() => setDisplayTab("all")}
              >
                <AllIcon />
                <span>{t("pixelsAll")}</span>
              </button>
              <button
                type="button"
                className={`px-display-tab ${
                  displayTab === "popular" ? "is-active" : ""
                }`}
                aria-pressed={displayTab === "popular"}
                onClick={() => setDisplayTab("popular")}
              >
                <PopularIcon />
                <span>{t("pixelsPopular")}</span>
              </button>
            </div>
            {displayTab === "popular" && (
              <p className="px-display-hint">{t("pixelsPopularHint")}</p>
            )}
            <div className="px-display-board">
              {posts.length === 0 ? (
                <p className="px-display-empty">{t("pixelsDisplayEmpty")}</p>
              ) : (
                <div className="px-display-grid">
                  {posts.map((post, index) => (
                    <PixelPostCard
                      key={post.id}
                      post={post}
                      userId={userId}
                      heartInteractive
                      busy={busyHeartId === post.id}
                      onHeart={giveHeart}
                      heartLabel={t("pixelsHeart")}
                      unheartLabel={t("pixelsUnheart")}
                      rank={displayTab === "popular" ? index + 1 : null}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : isAlbum ? (
          <div className="px-display">
            <div className="px-display-board">
              {posts.length === 0 ? (
                <p className="px-display-empty">{t("pixelsAlbumEmpty")}</p>
              ) : (
                <div className="px-display-grid">
                  {posts.map((post) => (
                    <PixelPostCard
                      key={post.id}
                      post={post}
                      userId={userId}
                      heartInteractive={false}
                      canDelete
                      busy={busyDeleteId === post.id}
                      onDelete={removePost}
                      deleteLabel={t("pixelsDelete")}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="px-blank" />
        )}
      </div>
    </div>
  );
};

export default PixelsPage;
